import type { MRT_ColumnDef } from '@talentmesh/table';
import { useMemo } from 'react';
import { ShortlistedProfile } from '../../../../api/DTO/ShortlistedProfile';
import CompanyCell from '../../../TableCells/CompanyCell';
import { useOutreachCandidatePopoutContext } from '../../Outreach/OutreachContext';
import simplifyShortlistedProfile from '../simplifyShortlistedProfile';
import CandidateCell from './CandidateCell';
import ContactInfoCell from './ContactInfoCell';
import EmailRepliedCell from './EmailRepliedCell';
import StatusCell from './StatusCell';
import TotalEmailsCell from './TotalEmailsCell';
import ScoreCell from '@/Components/Table/ScoreCell';
import TextCell from '@/Pages/Candidates/Overview/Contexts/CandidatesTableContext/cells/TextCell';
import toDateString from '@/Utils/DateHelper';
import UIStrings from '@/Utils/UIStrings';

export type ShortlistedCandidatesCol = MRT_ColumnDef<ShortlistedProfile>;

const useColumns = () => {
    const { openSendEmailPopout: openOutreachPopout } = useOutreachCandidatePopoutContext();

    return useMemo(() => {
        const columns: ShortlistedCandidatesCol[] = [
            {
                id: 'name',
                accessorFn: (row) => row.profile.name,
                header: UIStrings.Candidate,
                Cell: ({ row }) => <CandidateCell {...row.original} />,
                size: 200,
            },
            {
                id: 'contactInfo',
                header: UIStrings.ContactInfo,
                Cell: ({ row }) => (
                    <ContactInfoCell
                        {...row.original}
                        onMailClick={
                            !row.original.emailSentCount ? () => openOutreachPopout([row.original.profile]) : 'disabled'
                        }
                    />
                ),
                size: 130,
            },
            {
                id: 'status',
                header: UIStrings.Status,
                Cell: (params) => <StatusCell {...params} />,
                size: 220,
            },
            {
                id: 'company',
                header: UIStrings.Company,
                Cell: ({ row }) => <CompanyCell {...row.original} />,
                size: 300,
            },
            {
                id: 'position',
                accessorFn: (row) => row.profile.position,
                header: UIStrings.JobTitle,
                Cell: ({ row }) => <TextCell value={simplifyShortlistedProfile(row.original).jobTitle} />,
                size: 300,
            },
            {
                id: 'location',
                accessorFn: (row) => row.profile.currentJobLocationGeocode?.title,
                header: UIStrings.Location,
                Cell: ({ row, renderedCellValue }) => (
                    <TextCell
                        value={(renderedCellValue as string) || row.original.profile.locationGeocode?.title || ''}
                    />
                ),
                size: 300,
            },
            {
                id: 'matchScore',
                header: UIStrings.MatchScore,
                Cell: ({ row }) => (
                    <ScoreCell level={row.original.matchScore?.scoreLevel} score={row.original.matchScore?.score} />
                ),
                size: 120,
            },
            {
                id: 'emailSentCount',
                header: UIStrings.TotalEmails,
                accessorFn: ({ emailSentCount }) => emailSentCount ?? '-',
                Cell: ({ renderedCellValue }) => <TotalEmailsCell value={renderedCellValue} />,
                size: 120,
            },
            {
                id: 'lastEmailAt',
                header: UIStrings.LastEmail,
                accessorFn: ({ lastEmailAt }) => (lastEmailAt ? toDateString(new Date(lastEmailAt)) : '-'),
                Cell: ({ renderedCellValue }) => <TextCell value={renderedCellValue as string} />,
                size: 120,
            },
            {
                id: 'repliedAt',
                header: UIStrings.Replied,
                Cell: (params) => <EmailRepliedCell {...params} />,
                size: 120,
            },
        ];

        return columns;
    }, [openOutreachPopout]);
};

export default useColumns;
